import { hasAiFeatureAccess, hasAiFeatureAccessBySchool } from '_helpers/aiFeatureAccess';

/**
 * @typedef {Object} NavItem
 * @property {string}    id
 * @property {string}    label
 * @property {string}    [icon]
 * @property {string}    [path]
 * @property {string}    [externalUrl]
 * @property {NavItem[]} [children]
 * @property {string[]}  [matchPaths]
 * @property {boolean}   [requireSchool]
 * @property {boolean}   [hidden]
 * @property {Function}  [condition]
 */

export const ROLES = {
  STUDENT: 'student',
  TEACHER: 'teacher',
  SCHOOL: 'school'
};

/** @type {Record<string, { menu: NavItem[], allowedPaths: string[] }>} */
export const NAVIGATION_CONFIG = {
  [ROLES.STUDENT]: {
    menu: [
      {
        id: 'phongthitructuyen',
        label: 'Phòng thi trực tuyến',
        icon: 'icon-home',
        path: '/phong-thi-truc-tuyen'
      },
      {
        id: 'dethicanlam',
        label: 'Bài thi cần làm',
        icon: 'icon-open-book',
        path: '/de-thi-can-lam'
      },
      {
        id: 'lichsuthi',
        label: 'Lịch sử thi',
        icon: 'icon-presentation',
        path: '/lich-su-thi'
      },
      {
        id: 'huongdan',
        label: 'Hướng dẫn sử dụng',
        icon: 'icon-question',
        path: '/huong-dan'
      }
    ],
    allowedPaths: [
      '/phong-thi-truc-tuyen',
      '/de-thi-can-lam',
      '/lich-su-thi',
      '/huong-dan',
      '/thi',
      '/thi-thpt-2025',
      '/xem-ket-qua',
      '/xem-ket-qua-2025',
      '/thi-thu',
      '/thi-thu-thpt-qg',
      '/thong-tin-tai-khoan'
    ]
  },

  [ROLES.SCHOOL]: {
    menu: [
      {
        id: 'quanly',
        label: 'Quản lý',
        icon: 'icon-businessman',
        children: [
          {
            id: 'danhsachgiaovien',
            label: 'Danh sách giáo viên',
            path: '/danh-sach-giao-vien'
          },
          {
            id: 'danhsachhocsinh',
            label: 'Danh sách học sinh',
            path: '/khoi',
            matchPaths: ['/khoi', '/lop', '/chi-tiet-lop']
          },
          {
            id: 'quanlykythi',
            label: 'Quản lý kỳ thi',
            path: '/quan-ly-ky-thi'
          },
          {
            id: 'quanlydiem',
            label: 'Quản lý điểm',
            path: '/quan-ly-diem',
            matchPaths: ['/quan-ly-diem', '/diem-danh-sach', '/chi-tiet-diem']
          },
          {
            id: 'danhsachhocsinhphong',
            label: 'DS học sinh trong phòng',
            path: '/step-them-hoc-sinh-phong'
          },
          {
            id: 'danhsachgiaovienquanlylop',
            label: 'DS giáo viên quản lý lớp',
            path: '/danh-sach-giao-vien-quan-ly-lop'
          }
        ]
      },
      {
        id: 'quanlythituluan',
        label: 'Quản lý thi tự luận',
        icon: 'icon-businessman',
        children: [
          {
            id: 'danhsachbaithituluan',
            label: 'Danh sách bài thi tự luận',
            path: '/danh-sach-bai-thi-tu-luan',
            matchPaths: [
              '/danh-sach-bai-thi-tu-luan',
              '/danh-sach-de-thi-tu-luan',
              '/danh-sach-chua-cham-diem',
              '/danh-sach-da-cham-diem'
            ]
          }
        ]
      },
      {
        id: 'tochucthitructuyen',
        label: 'Tổ chức thi trực tuyến',
        icon: 'icon-video-conference',
        path: '/khoi-tao-phong-thi',
        requireSchool: true,
        matchPaths: [
          '/khoi-tao-phong-thi',
          '/step-them-hoc-sinh',
          '/step-chuyen-de'
        ]
      },
      {
        id: 'xulytinhhuongthi',
        label: 'Xử lý tình huống thi',
        icon: 'icon-presentation',
        path: '/xu-ly-tinh-huong-thi'
      },
      {
        id: 'quanlydethi',
        label: 'Quản lý đề thi',
        icon: 'icon-book',
        children: [
          {
            id: 'danhsachdethi',
            label: 'Danh sách đề thi',
            path: '/de-thi',
            matchPaths: ['/de-thi', '/danh-sach-cau-hoi']
          },
          {
            id: 'danhsachdethidachuyen',
            label: 'Danh sách đề thi đã chuyển',
            path: '/de-thi-da-chuyen'
          },
          {
            id: 'uploaddethi',
            label: 'Tạo mới đề thi',
            path: '/step-upload-de-thi?id=0&type=1',
            matchPaths: ['/step-upload-de-thi']
          }
        ]
      },
      {
        id: 'dethiai',
        label: 'Đề thi AI',
        icon: 'icon-library',
        children: [
          {
            id: 'quanlyaicredit',
            label: 'Quản lý AI Credits',
            path: '/quan-ly-ai-credits',
            matchPaths: ['/quan-ly-ai-credits']
          },
          {
            id: 'quanlydethiai',
            label: 'Quản lý đề thi AI',
            path: '/quan-ly-de-thi-ai',
            matchPaths: ['/quan-ly-de-thi-ai', '/tao-de-thi-ai']
          }
        ]
      },
      {
        id: 'quanlyphieutraloi',
        label: 'Chấm bài giấy',
        icon: 'icon-write',
        children: [
          {
            id: 'mauphieucham',
            label: 'DS mẫu phiếu chấm',
            path: '/danh-sach-mau-phieu-cham'
          },
          {
            id: 'xulyphieutraloi',
            label: 'Quản lý đợt chấm',
            path: '/xu-ly-phieu-tra-loi',
            matchPaths: ['/xu-ly-phieu-tra-loi']
          },
          {
            id: 'thongke',
            label: 'Thống kê',
            path: '/thong-ke'
          }
        ]
      },
      {
        id: 'systemlms360',
        label: 'Hệ thống QLHT LMS360',
        icon: 'icon-presentation',
        externalUrl: 'https://lms360.vn/',
        condition: (user) => {
          const TRAN_HUY_LIEU_SCHOOL_CODE = '79768507';
          return user?.schools?.[0]?.code === TRAN_HUY_LIEU_SCHOOL_CODE;
        }
      },
      {
        id: 'huongdan',
        label: 'Hướng dẫn sử dụng',
        icon: 'icon-question',
        path: '/huong-dan'
      }
    ],
    allowedPaths: [
      '/',
      '/khoi',
      '/khoi-tao-phong-thi',
      '/step-them-hoc-sinh',
      '/step-them-hoc-sinh-phong',
      '/step-upload-de-thi',
      '/step-upload-de-thi-thptqg',
      '/step-chuyen-de',
      '/lop',
      '/chi-tiet-lop',
      '/xu-ly-tinh-huong-thi',
      '/quan-ly-diem',
      '/quan-ly-danh-sach-diem',
      '/diem-danh-sach',
      '/chi-tiet-diem',
      '/de-thi',
      '/danh-sach-cau-hoi',
      '/cap-nhat-cau-hoi',
      '/cap-nhat-cau-hoi-thptqg',
      '/quan-ly-ky-thi',
      '/quan-ly-mon-hoc',
      '/chi-tiet-bai-thi',
      '/chi-tiet-bai-thi-thptqg',
      '/hanh-trinh-lam-bai',
      '/thi-thu',
      '/thi-thu-thpt-qg',
      '/danh-sach-bai-thi-tu-luan',
      '/danh-sach-de-thi-tu-luan',
      '/danh-sach-chua-cham-diem',
      '/danh-sach-da-cham-diem',
      '/danh-sach-giao-vien',
      '/in-bai-thi',
      '/de-thi-da-chuyen',
      '/danh-sach-giao-vien-quan-ly-lop',
      '/tao-de-thi-tu-ngan-hang',
      '/tao-de-thi-ai',
      '/quan-ly-de-thi-ai',
      '/quan-ly-ai-credits',
      '/gop-y-exam-ai',
      '/xu-ly-phieu-tra-loi',
      '/mau-phieu-cham',
      '/danh-sach-mau-phieu-cham',
      '/thong-ke',
      '/thong-tin-tai-khoan',
      '/testing',
      '/huong-dan',
      '/lich-su-thi',
      '/xem-ket-qua',
      '/xem-ket-qua-2025'
    ]
  },

  [ROLES.TEACHER]: {
    menu: [
      {
        id: 'quanly',
        label: 'Quản lý',
        icon: 'icon-businessman',
        children: [
          {
            id: 'danhsachhocsinh',
            label: 'Quản lý lớp của trường',
            path: '/khoi',
            matchPaths: ['/khoi', '/lop', '/chi-tiet-lop']
          },
          {
            id: 'quanlykythi',
            label: 'Quản lý kỳ thi',
            path: '/quan-ly-ky-thi'
          },
          {
            id: 'quanlydiem',
            label: 'Quản lý điểm',
            path: '/quan-ly-diem',
            matchPaths: ['/quan-ly-diem', '/diem-danh-sach', '/chi-tiet-diem']
          }
        ]
      },
      {
        id: 'quanlythituluan',
        label: 'Chấm bài thi tự luận',
        icon: 'icon-edit-1',
        children: [
          {
            id: 'danhsachbaithituluan',
            label: 'Chấm bài thi của trường',
            path: '/danh-sach-bai-thi-tu-luan',
            matchPaths: [
              '/danh-sach-bai-thi-tu-luan',
              '/danh-sach-de-thi-tu-luan',
              '/danh-sach-chua-cham-diem',
              '/danh-sach-da-cham-diem'
            ]
          },
          {
            id: 'danhsachbaithituluanlop',
            label: 'Chấm bài thi của lớp',
            path: '/danh-sach-bai-thi-tu-luan-lop',
            matchPaths: [
              '/danh-sach-bai-thi-tu-luan-lop',
              '/danh-sach-chua-cham-diem-lop',
              '/danh-sach-da-cham-diem-lop'
            ]
          }
        ]
      },
      {
        id: 'tochucthitructuyen',
        label: 'Tổ chức thi trực tuyến',
        icon: 'icon-video-conference',
        path: '/khoi-tao-phong-thi',
        requireSchool: true,
        matchPaths: [
          '/khoi-tao-phong-thi',
          '/step-them-hoc-sinh',
          '/step-chuyen-de'
        ]
      },
      {
        id: 'xulytinhhuongthi',
        label: 'Xử lý tình huống thi',
        icon: 'icon-presentation',
        path: '/xu-ly-tinh-huong-thi'
      },
      {
        id: 'quanlydethi',
        label: 'Quản lý đề thi',
        icon: 'icon-book',
        children: [
          {
            id: 'danhsachdethi',
            label: 'Danh sách đề thi',
            path: '/de-thi',
            matchPaths: ['/de-thi', '/danh-sach-cau-hoi']
          },
          {
            id: 'danhsachdethidachuyen',
            label: 'Danh sách đề thi đã chuyển',
            path: '/de-thi-da-chuyen'
          },
          {
            id: 'uploaddethi',
            label: 'Tạo mới đề thi',
            path: '/step-upload-de-thi?id=0&type=1',
            matchPaths: ['/step-upload-de-thi']
          }
        ]
      },
      {
        id: 'dethiai',
        label: 'Đề thi AI',
        icon: 'icon-library',
        children: [
          {
            id: 'quanlydethiai',
            label: 'Quản lý đề thi AI',
            path: '/quan-ly-de-thi-ai',
            matchPaths: ['/quan-ly-de-thi-ai', '/tao-de-thi-ai']
          },
          {
            id: 'gopydethiai',
            label: 'Góp ý Exam AI',
            path: '/gop-y-exam-ai',
            matchPaths: ['/gop-y-exam-ai'],
            condition: (user) => hasAiFeatureAccess(user)
          }
        ],
        condition: (user) => hasAiFeatureAccessBySchool(user)
      },
      {
        id: 'quanlyphieutraloi',
        label: 'Chấm bài giấy',
        icon: 'icon-write',
        children: [
          {
            id: 'mauphieucham',
            label: 'DS mẫu phiếu chấm',
            path: '/danh-sach-mau-phieu-cham'
          },
          {
            id: 'xulyphieutraloi',
            label: 'Quản lý đợt chấm',
            path: '/xu-ly-phieu-tra-loi',
            matchPaths: ['/xu-ly-phieu-tra-loi']
          },
          {
            id: 'thongke',
            label: 'Thống kê',
            path: '/thong-ke'
          }
        ]
      },
      {
        id: 'systemlms360',
        label: 'Hệ thống QLHT LMS360',
        icon: 'icon-presentation',
        externalUrl: 'https://lms360.vn/',
        condition: (user) => {
          const TRAN_HUY_LIEU_SCHOOL_CODE = '79768507';
          return user?.schools?.[0]?.code === TRAN_HUY_LIEU_SCHOOL_CODE;
        }
      },
      {
        id: 'huongdan',
        label: 'Hướng dẫn sử dụng',
        icon: 'icon-question',
        path: '/huong-dan'
      }
    ],
    allowedPaths: [
      '/',
      '/khoi',
      '/khoi-tao-phong-thi',
      '/step-them-hoc-sinh',
      '/step-them-hoc-sinh-phong',
      '/step-upload-de-thi',
      '/step-upload-de-thi-thptqg',
      '/step-chuyen-de',
      '/lop',
      '/chi-tiet-lop',
      '/xu-ly-tinh-huong-thi',
      '/quan-ly-diem',
      '/quan-ly-danh-sach-diem',
      '/diem-danh-sach',
      '/chi-tiet-diem',
      '/de-thi',
      '/danh-sach-cau-hoi',
      '/cap-nhat-cau-hoi',
      '/cap-nhat-cau-hoi-thptqg',
      '/quan-ly-ky-thi',
      '/quan-ly-mon-hoc',
      '/chi-tiet-bai-thi',
      '/chi-tiet-bai-thi-thptqg',
      '/hanh-trinh-lam-bai',
      '/thi-thu',
      '/thi-thu-thpt-qg',
      '/danh-sach-bai-thi-tu-luan',
      '/danh-sach-bai-thi-tu-luan-lop',
      '/danh-sach-de-thi-tu-luan',
      '/danh-sach-chua-cham-diem',
      '/danh-sach-chua-cham-diem-lop',
      '/danh-sach-da-cham-diem',
      '/danh-sach-da-cham-diem-lop',
      '/in-bai-thi',
      '/de-thi-da-chuyen',
      '/tao-de-thi-tu-ngan-hang',
      '/tao-de-thi-ai',
      '/quan-ly-de-thi-ai',
      '/gop-y-exam-ai',
      '/xu-ly-phieu-tra-loi',
      '/mau-phieu-cham',
      '/danh-sach-mau-phieu-cham',
      '/thong-ke',
      '/thong-tin-tai-khoan',
      '/testing',
      '/cham-diem-bai-thi',
      '/huong-dan',
      '/lich-su-thi',
      '/xem-ket-qua',
      '/xem-ket-qua-2025'
    ]
  }
};

/**
 * @param {Object} roles
 * @returns {string|null}
 */
export const getPrimaryRole = (roles) => {
  if (!roles) return null;
  if (roles.school) return ROLES.SCHOOL;
  if (roles.teacher) return ROLES.TEACHER;
  if (roles.student) return ROLES.STUDENT;
  return null;
};

/**
 * @param {string} role
 * @returns {{ menu: NavItem[], allowedPaths: string[] } | null}
 */
export const getNavConfig = (role) => NAVIGATION_CONFIG[role] || null;

/**
 * @param {string} role
 * @param {string} pathname
 * @returns {boolean}
 */
export const isPathAllowed = (role, pathname) => {
  const config = getNavConfig(role);
  if (!config) return false;
  return config.allowedPaths.some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
};

/**
 * @typedef {Object} RouteDefinition
 * @property {string}   path
 * @property {string}   component
 * @property {'main'|'minimal'} layout
 * @property {boolean}  [exact]
 * @property {string[]} [roles]
 */

export const PUBLIC_ROUTES = [
  { path: '/', component: 'Home', layout: 'minimal', exact: true },
  { path: '/dang-nhap', component: 'SignIn', layout: 'minimal' },
  { path: '/thoat', component: 'SignOut', layout: 'minimal' },
  {
    path: '/quen-mat-khau',
    component: 'ForgotPassword',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/dang-ky-tai-khoan',
    component: 'Register',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/dang-ky-tai-khoan-otp',
    component: 'RegisterOTP',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/tao-mat-khau',
    component: 'RegisterPassword',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/dang-ky-thong-tin',
    component: 'RegisterInfoPhone',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/cap-nhat-mat-khau',
    component: 'ChangePasswordOTP',
    layout: 'minimal',
    exact: true
  },
  {
    path: '/doi-mat-khau',
    component: 'ChangePasswordUser',
    layout: 'minimal',
    exact: true
  },
  { path: '/not-found', component: 'NotFound', layout: 'minimal', exact: true },
  { path: '/sso-error', component: 'SsoError', layout: 'minimal', exact: true },
  {
    path: '/not-support-role',
    component: 'NotSupportRole',
    layout: 'minimal',
    exact: true
  }
];

export const PROTECTED_ROUTES = [
  {
    path: '/thong-tin-tai-khoan',
    component: 'UpdateUserInfo',
    layout: 'minimal',
    exact: true
  },

  {
    path: '/phong-thi-truc-tuyen',
    component: 'StudentIndex',
    layout: 'main',
    exact: true
  },
  {
    path: '/lich-su-thi',
    component: 'StudentHistory',
    layout: 'main',
    exact: true
  },
  { path: '/thi', component: 'StudentExamModule', layout: 'main', exact: true },
  {
    path: '/thi-thpt-2025',
    component: 'StudentExamModule',
    layout: 'main',
    exact: true
  },
  {
    path: '/webview/thi',
    component: 'StudentExamModule',
    layout: 'bare',
    exact: true
  },
  {
    path: '/webview/thi-thpt-2025',
    component: 'StudentExamModule',
    layout: 'bare',
    exact: true
  },
  {
    path: '/webview/xem-ket-qua',
    component: 'StudentViewExam',
    layout: 'bare',
    exact: true
  },
  {
    path: '/webview/xem-ket-qua-2025',
    component: 'StudentViewExamTHPT2025',
    layout: 'bare',
    exact: true
  },
  {
    path: '/xem-ket-qua',
    component: 'StudentViewExam',
    layout: 'main',
    exact: true
  },
  {
    path: '/xem-ket-qua-2025',
    component: 'StudentViewExamTHPT2025',
    layout: 'main',
    exact: true
  },
  {
    path: '/de-thi-can-lam',
    component: 'TeacherDeThiCanLam',
    layout: 'main',
    exact: true
  },

  {
    path: '/thi-thu',
    component: 'TeacherTryExam',
    layout: 'main',
    exact: true
  },
  {
    path: '/thi-thu-thpt-qg',
    component: 'TeacherTryExamTHPTQG',
    layout: 'main',
    exact: true
  },

  { path: '/khoi', component: 'TeacherKhoi', layout: 'main', exact: true },
  {
    path: '/khoi-tao-phong-thi',
    component: 'TeacherKhoiTaoPhongThi',
    layout: 'main',
    exact: true
  },
  { path: '/lop', component: 'TeacherLop', layout: 'main', exact: true },
  {
    path: '/chi-tiet-lop',
    component: 'TeacherLopChiTiet',
    layout: 'main',
    exact: true
  },
  {
    path: '/step-them-hoc-sinh',
    component: 'TeacherStepThemHocSinh',
    layout: 'main',
    exact: true
  },
  {
    path: '/step-them-hoc-sinh-phong',
    component: 'TeacherStepThemHocSinhPhong',
    layout: 'main',
    exact: true
  },
  {
    path: '/step-chuyen-de',
    component: 'TeacherStepChuyenDe',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-giao-vien',
    component: 'TeacherListTeacher',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-giao-vien-quan-ly-lop',
    component: 'TeacherManageClass',
    layout: 'main',
    exact: true
  },

  {
    path: '/xu-ly-tinh-huong-thi',
    component: 'TeacherXuLyTinhHuongThi',
    layout: 'main',
    exact: true
  },

  {
    path: '/quan-ly-ky-thi',
    component: 'TeacherQLKyThi',
    layout: 'main',
    exact: true
  },
  {
    path: '/quan-ly-mon-hoc',
    component: 'TeacherQLMon',
    layout: 'main',
    exact: true
  },

  {
    path: '/quan-ly-diem',
    component: 'PointManagement',
    layout: 'main',
    exact: true
  },
  {
    path: '/diem-danh-sach',
    component: 'TeacherDiemDanhSach',
    layout: 'main',
    exact: true
  },
  {
    path: '/chi-tiet-diem',
    component: 'TeacherDiemChiTiet',
    layout: 'main',
    exact: true
  },

  { path: '/de-thi', component: 'TeacherDeThi', layout: 'main', exact: true },
  {
    path: '/danh-sach-cau-hoi',
    component: 'TeacherDeThiDanhSachCauHoi',
    layout: 'main',
    exact: true
  },
  {
    path: '/cap-nhat-cau-hoi',
    component: 'TeacherDeThiDanhSachCauHoiCapNhat',
    layout: 'main',
    exact: true
  },
  {
    path: '/cap-nhat-cau-hoi-thptqg',
    component: 'TeacherDeThiDanhSachCauHoiCapNhatTHPT2025',
    layout: 'main',
    exact: true
  },
  {
    path: '/step-upload-de-thi',
    component: 'TeacherStepUploadDe',
    layout: 'main',
    exact: true
  },
  {
    path: '/step-upload-de-thi-thptqg',
    component: 'TeacherStepUploadDeTHPTQG',
    layout: 'main',
    exact: true
  },
  {
    path: '/tao-de-thi-tu-ngan-hang',
    component: 'TeacherTaoDeThiTuNganHang',
    layout: 'main',
    exact: true
  },
  {
    path: '/tao-de-thi-ai',
    component: 'Exam2025Create',
    layout: 'main',
    exact: true
  },
  {
    path: '/quan-ly-de-thi-ai',
    component: 'Exam2025Manage',
    layout: 'main',
    exact: true
  },
  {
    path: '/quan-ly-ai-credits',
    component: 'ManageAICredits',
    layout: 'main',
    exact: true
  },
  {
    path: '/gop-y-exam-ai',
    component: 'ManageAIFeedbacks',
    layout: 'main',
    exact: true
  },
  {
    path: '/de-thi-da-chuyen',
    component: 'TeacherListTransfer',
    layout: 'main',
    exact: true
  },

  {
    path: '/chi-tiet-bai-thi',
    component: 'TeacherViewExam',
    layout: 'main',
    exact: true
  },
  {
    path: '/chi-tiet-bai-thi-thptqg',
    component: 'TeacherViewExamTHPTQG',
    layout: 'main',
    exact: true
  },
  {
    path: '/hanh-trinh-lam-bai',
    component: 'TeacherHanhTrinhLamBai',
    layout: 'main',
    exact: true
  },
  {
    path: '/in-bai-thi',
    component: 'TeacherPrintExam',
    layout: 'main',
    exact: true
  },

  {
    path: '/danh-sach-bai-thi-tu-luan',
    component: 'TeacherIndexEssay',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-bai-thi-tu-luan-lop',
    component: 'TeacherIndexEssay',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-chua-cham-diem',
    component: 'TeacherChuaChamDiem',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-chua-cham-diem-lop',
    component: 'TeacherChuaChamDiem',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-da-cham-diem',
    component: 'TeacherDaChamDiem',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-da-cham-diem-lop',
    component: 'TeacherDaChamDiem',
    layout: 'main',
    exact: true
  },
  {
    path: '/danh-sach-de-thi-tu-luan',
    component: 'TeacherDeThiTuLuan',
    layout: 'main',
    exact: true
  },
  {
    path: '/cham-diem-bai-thi',
    component: 'TeacherScoreExamEssay',
    layout: 'main',
    exact: true
  },

  {
    path: '/danh-sach-mau-phieu-cham',
    component: 'TeacherDSMauPhieuCham',
    layout: 'main',
    exact: true
  },
  {
    path: '/mau-phieu-cham',
    component: 'TeacherMauPhieuCham',
    layout: 'main',
    exact: true
  },
  {
    path: '/xu-ly-phieu-tra-loi',
    component: 'TeacherXuLyPhieuTraLoi',
    layout: 'main',
    exact: true
  },
  {
    path: '/xu-ly-phieu-tra-loi/:sessionId',
    component: 'GradingRoundDetailView',
    layout: 'main',
    exact: true
  },

  { path: '/thong-ke', component: 'Statistical', layout: 'main', exact: true },
  { path: '/testing', component: 'PageTesting', layout: 'main', exact: true },
  { path: '/huong-dan', component: 'Guide', layout: 'main', exact: true }
];
